<script setup>
import { computed } from 'vue'
import Pts2ChecklistFilter from './Pts2ChecklistFilter.vue'
import Pts2HistoryFilters from './Pts2HistoryFilters.vue'
import Pts2PagedFilter from './Pts2PagedFilter.vue'
import { useI18n } from '../../i18n.js'
import { facetItemsWithSelection } from '../../pts2/filterOptions.js'

const model = defineModel({ type: Object, required: true })
const props = defineProps({
  options: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  rangeError: { type: String, default: '' },
})
const emit = defineEmits(['apply', 'clear', 'close', 'options', 'search', 'select'])
const { locale, t } = useI18n()

const fuels = computed(() => facetItemsWithSelection(props.options.FUEL))
const pumps = computed(() => facetItemsWithSelection(props.options.PUMP))
const nozzles = computed(() => facetItemsWithSelection(props.options.NOZZLE))
const tanks = computed(() => facetItemsWithSelection(props.options.TANK))

function shiftLabel(option) {
  if (option.metadataState === 'AMBIGUOUS') return t('pts2UiHistoryShiftAmbiguous', { number: option.number })
  const start = option.shiftStartedAt ? formatDate(option.shiftStartedAt) : null
  const end = option.shiftEndedAt ? formatDate(option.shiftEndedAt) : null
  if (option.shiftState === 'OPEN_AS_OBSERVED' && start) return t('pts2UiHistoryShiftOpen', { number: option.number, time: start })
  return `№${option.number}${start ? ` · ${start}${end ? ` → ${end}` : ''}` : ''}`
}

function tagLabel(option) {
  const tag = option.tag ?? {}
  return `${tag.name || t('pts2UiHistoryUnknownOwner')} · ${tag.value || tag.maskedValue || t('pts2UiHistoryIdUnavailable')}`
}

const tankLabel = (option) => `№${option.number}`
const pumpLabel = (option) => t('pts2UiHistoryPumpNumber', { number: option.number })
const nozzleLabel = (option) => `№${option.number}`
const fuelLabel = (option) => option.name || t('pts2UiHistoryFuelNumber', { number: option.number })

function replaceSelection(field, selection) {
  const target = model.value[field]
  target.splice(0, target.length, ...selection)
}

function formatDate(value) {
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}
</script>

<template>
  <form novalidate class="pts2-transaction-filters" @submit.prevent="emit('apply')" @keydown.esc.prevent="emit('close')" @keydown.enter="$event.target.tagName === 'BUTTON' || $event.preventDefault()">
    <Pts2HistoryFilters v-model="model" :loading="loading" :show-source="false" :range-error="rangeError" include-seconds inclusive-end />
    <div class="pts2-filter-selects">
      <Pts2PagedFilter v-model="model.tagSelection" :label="t('pts2UiHistoryTagLabel')" :state="options.TAG" searchable
        :disabled="loading" :option-value="(option) => `REF:${option.ref}`" :option-label="tagLabel"
        :static-options="[{ value: 'ALL', label: t('pts2UiHistoryAllTags') }, ...(options.TAG.hasUnassigned ? [{ value: 'ABSENT', label: t('pts2UiHistoryNoTag') }] : []), ...(options.TAG.hasUnavailable ? [{ value: 'UNAVAILABLE', label: t('pts2UiHistoryTagUnavailable') }] : [])]"
        @load="(cursor, force) => emit('options', 'TAG', cursor, force)" @search="emit('search', 'TAG', $event)" @select="emit('select', 'TAG', $event)" />
      <Pts2ChecklistFilter :model-value="model.tankNumbers" :label="t('pts2UiHistoryTankNumberLabel')" :options="tanks"
        @update:model-value="replaceSelection('tankNumbers', $event)"
        :placeholder="t('pts2UiHistoryAllTanks')" :disabled="loading || options.TANK.loading || Boolean(options.TANK.error)" :option-label="tankLabel" />
      <Pts2ChecklistFilter :model-value="model.pumpNumbers" :label="t('pts2UiHistoryPumpLabel')" :options="pumps"
        @update:model-value="replaceSelection('pumpNumbers', $event)"
        :placeholder="t('pts2UiHistoryAllPumps')" :disabled="loading || options.PUMP.loading || Boolean(options.PUMP.error)" :option-label="pumpLabel" />
      <Pts2ChecklistFilter :model-value="model.nozzleNumbers" :label="t('pts2UiHistoryNozzleNumberLabel')" :options="nozzles"
        @update:model-value="replaceSelection('nozzleNumbers', $event)"
        :placeholder="t('pts2UiHistoryAllNozzles')" :disabled="loading || options.NOZZLE.loading || Boolean(options.NOZZLE.error)" :option-label="nozzleLabel" />
      <Pts2PagedFilter v-model="model.shiftSelection" :label="t('pts2UiHistoryShiftNumberLabel')" :state="options.SHIFT" searchable
        :disabled="loading" :option-value="(option) => `NUMBER:${option.number}`" :option-label="shiftLabel"
        :static-options="[{ value: 'ALL', label: !options.SHIFT.loading && !options.SHIFT.error && !options.SHIFT.items.length ? t('pts2UiHistoryNoShifts') : t('pts2UiHistoryAllTransactions') }, ...(options.SHIFT.hasUnassigned ? [{ value: 'UNASSIGNED', label: t('pts2UiHistoryNoShift') }] : [])]"
        @load="(cursor, force) => emit('options', 'SHIFT', cursor, force)" @search="emit('search', 'SHIFT', $event)" @select="emit('select', 'SHIFT', $event)" />
      <Pts2ChecklistFilter :model-value="model.fuelGradeIds" :label="t('pts2UiHistoryFuel')" :options="fuels"
        @update:model-value="replaceSelection('fuelGradeIds', $event)"
        :placeholder="t('pts2UiHistoryAllFuels')" :disabled="loading || options.FUEL.loading || Boolean(options.FUEL.error)" appearance="entity" :option-label="fuelLabel" />
    </div>
    <div v-for="facet in ['TANK', 'PUMP', 'NOZZLE', 'FUEL']" :key="facet" class="pts2-facet-feedback">
      <small v-if="options[facet].loading" role="status">{{ t('pts2FilterOptionsLoading') }}</small>
      <template v-if="options[facet].error"><small class="field-error" role="alert">{{ t(options[facet].error) }}</small><button class="pc-secondary" type="button" @click="emit('options', facet, options[facet].requestedCursor, true)">{{ t('pts2FilterRetry') }}</button></template>
    </div>
    <footer>
      <button class="pc-secondary" type="button" :disabled="loading" @click="emit('clear')">{{ t('pts2UiHistoryClear') }}</button>
      <button class="pc-primary" type="submit" :disabled="loading">{{ loading ? t('pts2UiHistoryApplying') : t('pts2UiHistoryApply') }}</button>
    </footer>
  </form>
</template>
